





















import Vue, {VueConstructor} from 'vue'
import Messenger from '@/components/Messenger/Messenger.vue'
import {RawMessage} from '@/types/entity/RawMessage'
import Message from '@/models/Message/Message'
import _ from 'lodash'
import {Dictionary} from 'vuex'
const MSGS_LOAD_LIMIT = 7
const MSGS_WATCHER_PERIOD = 7000
export default (Vue as VueConstructor<Vue & {
  $refs: {
    messenger: {
      freezeScroll: (prevScrollHeight?: number) => void,
      getScrollHeight: () => number,
      slideScrollToBottom: () => void,
      clearMessageText: () => void,
      getVisibleGroups: () => Dictionary<Message[]>,
      setInputDisabled: (val: boolean) => void
    }
  }
}>).extend({
  components: {Messenger},
  props: {
    lsnId: String,
    hasPerm: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      list: [] as Message[],
      interval: null,
      isLoadingHistory: false,
      isHistoryEnded: false
    }
  },
  computed: {
    lastId (): string | null {
      if (this.list.length) {
        return this.list[this.list.length - 1].getId()
      } else {
        return null
      }
    },
    firstId (): string | null {
      if (this.list.length) {
        return this.list[0].getId()
      } else {
        return null
      }
    }
  },
  methods: {
    async preLoad (): Promise<void> {
      try {
        let msgs = await this.listLoader({limit: MSGS_LOAD_LIMIT})
        this.addMesseges(msgs)
        this.slideScrollToBottom()
      } catch (error) {
        console.error('preLoad: error', error)

        if (this.$store.state.user.user) {
          // @ts-ignore
          this.$message.warning(this.$t('messeger.can_not_load_messeges_history'))
        }
      }
    },
    async addLoad (): Promise<void> {
      if (this.isLoadingHistory || this.isHistoryEnded || !this.firstId) return
      this.isLoadingHistory = true
      try {
        let msgs = await this.listLoader({limit: MSGS_LOAD_LIMIT, after: false, msg_id: this.firstId || undefined})
        if (!msgs || !msgs.length) {
          this.isHistoryEnded = true
          return
        }
        // Capture the scroll height *before* prepending so freezeScroll can
        // restore the exact viewport position (delta = how far content moved
        // down). Must be read before addMesseges mutates the list.
        const prevScrollHeight = this.$refs.messenger.getScrollHeight()
        this.addMesseges(msgs)
        this.freezeScroll(prevScrollHeight);
      } catch (error) {
        console.error('addLoad: error', error)

        if (this.$store.state.user.user) {
          // @ts-ignore
          this.$message.warning(this.$t('messeger.can_not_load_messeges_history'))
        }
      } finally {
        this.isLoadingHistory = false
      }
    },
    async newLoad (): Promise<void> {
      try {
        let msgs = await this.listLoader({limit: MSGS_LOAD_LIMIT, after: true, msg_id: this.lastId || undefined})
        if (msgs && msgs.length) {
          this.addMesseges(msgs, true)
          this.slideScrollToBottom()
        }
      } catch (error) {
        // @ts-ignore
        this.$message.warning(this.$t('messeger.can_not_load_new_messages'))
      }
    },
    async updateData (): Promise<void> {
      try {
        const ids = []
        const visGroups = this.$refs.messenger.getVisibleGroups()
        for (const group in visGroups) {
          if (visGroups.hasOwnProperty(group)) {
            const messages = visGroups[group];
            for (const message of messages) {
              ids.push(message.getId())
            }
          }
        }
        if (ids.length) {
          await this.$store.dispatch('messages/getMessages', {msg_ids: ids})
        }
      } catch (error) {
        // @ts-ignore
        this.$message.info(this.$t('messeger.waiting_for_connection'))
      }
    },
    onScrollUp () {
      this.addLoad();
    },
    addMesseges (msgs: Message[], push = false) {
      if (msgs && msgs.length) {
        for (const msg of msgs) {
          if (push) {
            this.list.push(msg);
          } else {
            this.list.unshift(msg);
          }
        }
      }
    },
    async onInput (text: string) {
      try {
        this.$refs.messenger.setInputDisabled(true)
        const {msg_id: msdId} = await this.$store.dispatch('messages/postMessage', {text})
        if (msdId) {
          let messages = await this.$store.dispatch('messages/getMessages', {msg_ids: [msdId]})
          this.$refs.messenger.setInputDisabled(false)
          this.addMesseges(messages, true);
          this.$refs.messenger.clearMessageText();
          this.slideScrollToBottom();
        } else {
          throw new Error(`msg_id is ${msdId}`)
        }
      } catch (error) {
        this.$refs.messenger.setInputDisabled(false)
        // @ts-ignore
        this.$message.error(this.$t('messeger.can_not_send_message'))
      }
    },
    async listLoader (params?: any): Promise<Message[]> {
      let messeges = await this.$store.dispatch('messages/listMessages', {...params})
      return messeges
    },
    dataWatcher (): void {
      // @ts-ignore
      let self = this;
      // @ts-ignore
      self.interval = setInterval(() => {
        this.newLoad()
        this.updateData();
      }, MSGS_WATCHER_PERIOD)
    },
    slideScrollToBottom () {
      this.$nextTick(() => {
        if (this.$refs.messenger) {
          this.$refs.messenger.slideScrollToBottom();
        }
      })
    },
    freezeScroll (prevScrollHeight?: number) {
      // Use $nextTick (microtask) rather than requestAnimationFrame so that
      // the scrollTop adjustment runs in the same microtask flush as Vue's
      // DOM patch — guaranteed before any paint/vsync frame.  rAF can miss
      // a vsync in Safari (if the current frame's deadline has already
      // passed the rAF fires in the *next* frame), which causes a 1-frame
      // flash showing the prepended messages at scrollTop=0.
      this.$nextTick(() => {
        if (this.$refs.messenger) {
          this.$refs.messenger.freezeScroll(prevScrollHeight);
        }
      })
    },
    clearInterval (): void {
      // @ts-ignore
      clearInterval(this.interval)
    }
  },
  mounted () {
    if (this.hasPerm) {
      this.preLoad();
      this.dataWatcher();
    }

    this.$root.$on('relogin', this.clearInterval)
    this.$root.$on('on-login', this.dataWatcher)
  },
  beforeDestroy () {
    this.clearInterval()
    this.$root.$off('relogin', this.clearInterval)
    this.$root.$off('on-login', this.dataWatcher)
  }
})
